import { formatoFechaBolivia, formatoPrecio } from '@/lib/utils/formato-precio';
import type { ViaDelCambio } from '../types/price-proposals.types';
import type { CambioDelHistorial } from '../types/historial-de-precios.types';

/**
 * Lo que dice el historial de precios (lote C2b, spec 246 punto 11; maqueta
 * `c2-historial`), en palabras. Puro: el componente sólo pinta.
 */

/** Los filtros de la maqueta («Todos · Estrategias · A mano · Archivo · API») y uno para el resto. */
export type GrupoDeVia = 'ESTRATEGIAS' | 'A_MANO' | 'ARCHIVO' | 'API' | 'OTROS';

/**
 * En qué filtro cae cada vía (el registro de C2a lo dejó para C2b): el Precio
 * Inteligente viejo aplicaba una estrategia; volver al precio base, desvincular
 * y volver a un precio los hace una persona a mano; el menú, el catálogo leído
 * y la tienda demo van a «Otros».
 */
export const GRUPO_DE_LA_VIA: Record<ViaDelCambio, GrupoDeVia> = {
  APROBACION: 'ESTRATEGIAS',
  AUTOMATICA: 'ESTRATEGIAS',
  VENCIMIENTO: 'ESTRATEGIAS',
  ESTRATEGIA: 'ESTRATEGIAS',
  PRECIO_INTELIGENTE: 'ESTRATEGIAS',
  A_MANO: 'A_MANO',
  VOLVER: 'A_MANO',
  PRECIO_BASE: 'A_MANO',
  DESVINCULAR: 'A_MANO',
  CSV: 'ARCHIVO',
  CARGA_DE_DATOS: 'ARCHIVO',
  API: 'API',
  MENU: 'OTROS',
  CATALOGO: 'OTROS',
  TIENDA_DEMO: 'OTROS',
};

export const TEXTO_DEL_GRUPO: Record<GrupoDeVia, string> = {
  ESTRATEGIAS: 'Estrategias',
  A_MANO: 'A mano',
  ARCHIVO: 'Archivo',
  API: 'API',
  OTROS: 'Otros',
};

const ORDEN: GrupoDeVia[] = ['ESTRATEGIAS', 'A_MANO', 'ARCHIVO', 'API', 'OTROS'];

/** Los filtros con cuántos tiene cada uno: «Todos» y sólo los grupos que tienen alguno. */
export function filtrosDelHistorial(
  cambios: CambioDelHistorial[]
): Array<{ valor: 'TODOS' | GrupoDeVia; texto: string; cuenta: number }> {
  const cuenta = new Map<GrupoDeVia, number>();
  for (const c of cambios) {
    const g = GRUPO_DE_LA_VIA[c.via];
    cuenta.set(g, (cuenta.get(g) ?? 0) + 1);
  }
  return [
    { valor: 'TODOS', texto: 'Todos', cuenta: cambios.length },
    ...ORDEN.filter((g) => cuenta.has(g)).map((g) => ({
      valor: g,
      texto: TEXTO_DEL_GRUPO[g],
      cuenta: cuenta.get(g)!,
    })),
  ];
}

/** Cómo se llama cada vía en la columna «Quién o qué». La única tabla de
 * nombres: la fila abierta de Por decidir la reutiliza, para que la misma vía
 * se lea igual en las dos pantallas (revisión conjunta). */
export const TITULO_DE_LA_VIA: Record<ViaDelCambio, string> = {
  APROBACION: 'Aprobación de una propuesta',
  AUTOMATICA: 'Se aplicó sola',
  VENCIMIENTO: 'Volvió al vencer su estrategia',
  ESTRATEGIA: 'Aprobación de una propuesta',
  PRECIO_INTELIGENTE: 'Precio inteligente (el de antes)',
  A_MANO: 'A mano, en la ficha',
  VOLVER: 'Volvió a un precio anterior',
  PRECIO_BASE: 'Volvió al precio de lista',
  DESVINCULAR: 'Al desvincularlo',
  CSV: 'Archivo de productos',
  CARGA_DE_DATOS: 'Carga de datos por archivo',
  API: 'Por la API de Prixia',
  MENU: 'La lectura del menú de tu local',
  CATALOGO: 'Importado del catálogo leído',
  TIENDA_DEMO: 'Desde la tienda de demostración',
};

/**
 * «Quién o qué»: el título por la vía y, debajo, el origen, la persona y —en
 * modo soporte— quién actuó de verdad. «Aprobación de una propuesta» con
 * ««Sostener el margen objetivo» · Roberto Méndez».
 */
export function quienOQue(c: CambioDelHistorial): { titulo: string; detalle: string | null } {
  const titulo =
    c.via === 'ESTRATEGIA' && c.sola
      ? TITULO_DE_LA_VIA.AUTOMATICA
      : // A mano pero el producto nació con ese precio: no fue «a mano» (ronda 2).
        c.via === 'A_MANO' && c.antes == null
        ? 'Al crearlo'
        : TITULO_DE_LA_VIA[c.via];
  const partes: string[] = [];
  // Un deshacer (D4) guarda «Deshacer un cambio» como origen: no se repite,
  // se dice qué cambio deshizo.
  const deshizo = c.via === 'VOLVER' && c.deshizoEl != null;
  if (c.fuente && !deshizo) partes.push(c.via === 'API' ? `clave «${c.fuente}»` : `«${c.fuente}»`);
  if (deshizo) partes.push(`deshizo el cambio del ${formatoFechaBolivia(c.deshizoEl!)}`);
  else if (c.via === 'VOLVER' && c.volvioA) partes.push(`el del ${formatoFechaBolivia(c.volvioA)}`);
  // Una aprobación sin persona: se dice (maqueta), no se deja el lugar vacío.
  const esAprobacion =
    c.via === 'APROBACION' || c.via === 'VENCIMIENTO' || (c.via === 'ESTRATEGIA' && !c.sola);
  if (c.quien) partes.push(c.quien);
  else if (esAprobacion) partes.push('no quedó registrado quién');
  if (c.soporte) partes.push(`lo hizo ${c.soporte}, del soporte de Prixia`);
  return { titulo, detalle: partes.length > 0 ? partes.join(' · ') : null };
}

/** «18 nov 2026, 10:51», en hora de Bolivia (D10: se dice una vez, en la ventana). */
export const cuandoEnTexto = (iso: string) => formatoFechaBolivia(iso, { hora: true });

/**
 * «Antes del 3 oct 2026 sólo se guardaban…» (maqueta: el corte). Debajo también
 * aparecen volver al precio base y desvincular (el log viejo): el texto los
 * nombra (auditoría H8).
 */
export function textoDelCorte(registroDesde: string): string {
  return `Antes del ${formatoFechaBolivia(registroDesde)} sólo se guardaban los cambios de las estrategias, volver al precio de lista y desvincular. Los demás no se pueden recuperar.`;
}

const CENTAVO = 0.005;

/** El precio de esta fila es el de hoy (medio centavo de tolerancia, como todo el código). */
export const esElPrecioDeHoy = (c: CambioDelHistorial, hoy: number) =>
  Math.abs(c.despues - hoy) < CENTAVO;

/**
 * ¿Se ofrece «Volver a X»? Sólo un cambio del registro (lo de antes está
 * incompleto), que no sea de una estrategia (esos tienen «Por qué», maqueta) y
 * cuyo precio no sea el de hoy.
 */
export function sePuedeVolver(c: CambioDelHistorial, hoy: number): boolean {
  return c.cambioId != null && c.propuestaId == null && !esElPrecioDeHoy(c, hoy);
}

/** «Volver a 5.990,00». */
export const textoDeVolver = (c: CambioDelHistorial) => `Volver a ${formatoPrecio(c.despues)}`;

/**
 * El último cambio del registro: el primero con `cambioId` (la lista viene del
 * más nuevo al más viejo, y lo de antes del registro va al final).
 */
export const ultimoDelRegistro = (cambios: CambioDelHistorial[]) =>
  cambios.find((c) => c.cambioId != null) ?? null;

/**
 * ¿Se ofrece «Volver a X (el de antes)», o sea deshacer (ronda del 5-oct)? Sólo
 * el último cambio del registro, con precio de antes, que dejó el precio de hoy
 * (el mismo freno que el servidor, D2: si no, hubo otro cambio después). Una
 * aprobación sí: es lo que más se quiere deshacer.
 */
export function sePuedeDeshacer(c: CambioDelHistorial, hoy: number, esElUltimo: boolean): boolean {
  return (
    esElUltimo &&
    c.cambioId != null &&
    c.antes != null &&
    Math.abs(c.antes - c.despues) >= CENTAVO &&
    esElPrecioDeHoy(c, hoy)
  );
}

/** «Volver a 4.275,00 (el de antes)». */
export const textoDeDeshacer = (c: CambioDelHistorial) =>
  `Volver a ${formatoPrecio(c.antes ?? c.despues)} (el de antes)`;

/** Spec 275 (R1-e): qué hizo un VOLVER, en una frase. */
export function queHizoElVolver(c: CambioDelHistorial): string {
  if (c.deshizoEl) return `Deshizo el cambio del ${cuandoEnTexto(c.deshizoEl)}`;
  if (c.volvioA) return `Volvió al precio del ${cuandoEnTexto(c.volvioA)}`;
  return 'Volvió a un precio anterior';
}

/** ¿La fila ofrece «Por qué»? Las de una propuesta, los VOLVER y la del precio de hoy. */
export function tienePorQue(c: CambioDelHistorial, esHoy: boolean): boolean {
  return c.propuestaId != null || c.via === 'VOLVER' || esHoy;
}
